/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { branded, FragmentTranslations, LanguagePack, mapObject, Translations } from "@buzzeasy/shared-frontend-utilities";
import { MenuProps } from "antd";
import { translations } from "../../translations";

export const agentUITranslationsKey = "HostApp";

async function getFragmentTranslations(): Promise<Record<string, FragmentWithTranslations>> {
  return {
    [agentUITranslationsKey]: { translations: branded(mapObject(translations, ([lang, val]) => [lang, val.translation])) },
    ActivityHistoryFragment: await import("@buzzeasy/activity-history-fragment-2").then(m => m.default),
    CallControlFragment: await import("@buzzeasy/call-control-fragment-2").then(m => m.default),
    CallTranslatorFragment: await import("@buzzeasy/call-translator-fragment").then(m => m.default),
    CaseDataFragment: await import("@buzzeasy/case-data-fragment").then(m => m.default),
    ChatFragment: await import("@buzzeasy/chat-fragment-2").then(m => m.default),
    ContactLookupFragment: await import("@buzzeasy/contact-lookup-fragment"),
    CrmFragment: await import("@buzzeasy/crm-fragment").then(m => m.default),
    CustomerDataFragment: await import("@buzzeasy/customer-data-fragment-2").then(m => m.default),
    DirectoryFragment: await import("@buzzeasy/directory-fragment").then(m => m.default),
    EmailFragment: await import("@buzzeasy/email-fragment-2").then(m => m.default),
    MultimediaCallFragment: await import("@buzzeasy/multimedia-call-fragment").then(m => m.default),
    ScriptRenderer: await import("@buzzeasy/script-renderer").then(m => m.default),
    VirtualConversationFragment: await import("@buzzeasy/virtual-conversation-fragment-2").then(m => m.default),
  };
}

export const fragmentNameMap: { key: string; repo: string }[] = [
  { key: agentUITranslationsKey, repo: "AgentUI2" },
  { key: "ActivityHistoryFragment", repo: "ActivityHistoryFragment2" },
  { key: "CallControlFragment", repo: "CallControlFragment2" },
  { key: "CallTranslatorFragment", repo: "CallTranslatorUI" },
  { key: "CaseDataFragment", repo: "CaseDataFragment" },
  { key: "ChatFragment", repo: "ChatFragment2" },
  { key: "ContactLookupFragment", repo: "ContactLookupFragment" },
  { key: "CrmFragment", repo: "CrmFragment" },
  { key: "CustomerDataFragment", repo: "CustomerDataFragment2" },
  { key: "DirectoryFragment", repo: "DirectoryFragment2" },
  { key: "EmailFragment", repo: "EmailFragment2" },
  { key: "MultimediaCallFragment", repo: "MultimediaCallFragment" },
  { key: "ScriptRenderer", repo: "ScriptRenderer" },
  { key: "VirtualConversationFragment", repo: "VirtualConversationFragment2" },
];

export function toRepoName(key: string): string {
  const fragment = fragmentNameMap.find(item => item.key === key);

  if (fragment)
    return fragment.repo;
  else
    throw new Error(`Failed to find the repository name for '${key}'.`);
}

export type FragmentWithTranslations = object & { translations?: FragmentTranslations };

export type HostAppTranslations = Translations & { meta: { languageInEnglish: string; languageInNative: string } };
export type LanguagePackWithHostApp = LanguagePack & { [agentUITranslationsKey]: HostAppTranslations };

export async function getLanguagePack(lang = "en-US"): Promise<LanguagePack> {
  const result = {} as LanguagePack;

  Object.entries(await getFragmentTranslations()).forEach(([key, fragment]) => {
    result[key] = fragment.translations?.[lang] || {};
  });

  return result;
}

export async function getLanguagePackTemplate(): Promise<LanguagePack> {
  const result = await getLanguagePack();

  function recursiveGetEmptyValues(current: Translations | string): Translations | string {
    if (typeof current === "object" && current !== null) {
      return mapObject(current, ([key, value]) => [key, recursiveGetEmptyValues(value)]);
    }
    else {
      return "";
    }
  }

  return recursiveGetEmptyValues(result) as Awaited<ReturnType<typeof getLanguagePack>>;
}

export function getAllTranslationsFilledIn(obj: Translations): boolean {
  function recursiveCheckFilledIn(current: Translations | string): boolean {
    if (typeof current === "object" && current !== null) {
      return Object.values(current).every(value => recursiveCheckFilledIn(value));
    }
    else {
      return current.trim() !== "";
    }
  }

  return Object.values(obj).every(fragment => recursiveCheckFilledIn(fragment));
}

export function applyValuesToTranslationTemplate(values: LanguagePack): LanguagePack {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function recursiveApplyValues(current: any, value: any) {
    if (typeof current === "object") {
      const result = {} as Record<string, unknown>;
      Object.keys(current)
        .forEach(key => result[key] = recursiveApplyValues(current[key], value?.[key]));
      return result;
    }
    else {
      return value ?? "";
    }
  }

  return recursiveApplyValues(emptyTemplateTranslations, values);
}

export function getLanguagePackOptions(): NonNullable<MenuProps["items"]> {
  return Object.entries(translations)
    .map(([key, value]) => ({
      key,
      label: value.translation.meta.languageInEnglish,
    }));
}

export let placeholderTranslations: LanguagePack;
export let emptyTemplateTranslations: LanguagePack;

export const languagePackOptions = getLanguagePackOptions();
export const languageCodeRegex = /^([a-zA-Z]{2,})(-[a-zA-Z]{2,}){0,3}$/;

let initPromise: Promise<void> | null = null;

export async function initializeTranslationDefaultValues() {
  if (!initPromise) {
    let resolveInit: (() => void) | undefined;
    initPromise = new Promise<void>(resolve => resolveInit = resolve);

    placeholderTranslations = await getLanguagePack();
    emptyTemplateTranslations = await getLanguagePackTemplate();

    resolveInit?.();
  }

  return initPromise;
}
